import React, { forwardRef, useCallback, useState } from 'react';

import { gql } from '@apollo/client';
import type { PopoverProps } from '@mui/material';
import { ListItemText } from '@mui/material';
import type { GridApi } from '@mui/x-data-grid-premium';
import clsx from 'clsx';
import { AlineAvatar, AlineAvatarGroup } from 'src/components/AlineAvatar';
import { AlineCheckbox, AlineTooltip } from 'src/components/generic';
import { getAssignmentString } from 'src/components/send-for-signature/components/sanity-check-modal/unused-actions-copy';
import {
  AddCollaboratorDialogVariant,
  AddWorkflowCollaboratorDialog,
} from 'src/components/workflow/workflow-collaborators';
import type { CollaboratorOption } from 'src/components/workflow/workflow-collaborators/WorkflowCollaboratorsAutocomplete';
import {
  getCollaboratorDisplayDetails,
  NewCollaboratorOption,
  useCollabOptionStyles,
} from 'src/components/workflow/workflow-collaborators/WorkflowCollaboratorsAutocomplete';
import { WorkflowCollaboratorsPopper } from 'src/components/workflow/workflow-collaborators/WorkflowCollaboratorsPopper';
import { WORKFLOW_LAUNCHER_LABEL } from 'src/components/workflow/workflow-helpers';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { getPermissionAvatarInfo, useDispatchNotification } from 'src/helpers';
import { useGetCheckWorkflowAuthorization } from 'src/hooks';
import type { UserWithOrg } from 'src/types';
import type { WorkflowPermissionSubset } from 'src/types/apollo/workflow-permission';
import { makeStyles } from 'tss-react/mui';

interface StyleProps {
  numAssignees: number;
  selected: boolean;
}

const useButtonStyles = makeStyles<StyleProps>()((theme, { numAssignees, selected }) => ({
  button: {
    '&:hover': {
      backgroundColor: selected
        ? theme.appColors.selectedBackground
        : theme.appColors.hoverBackground,
    },
    alignItems: 'center',
    backgroundColor: selected ? theme.appColors.selectedBackground : undefined,
    borderRadius: [0, 1].includes(numAssignees) ? '50%' : 16,
    cursor: 'pointer',
    display: 'flex',
    padding: theme.spacing(0.5),
    width: 'fit-content',
  },
}));

interface WorkflowAssigneesButtonProps {
  assignees: WorkflowPermissionSubset[];
  onClick: (e: React.MouseEvent) => void;
  open: boolean;
  className?: string;
  dataCy?: string;
  max?: number;
}

/** Compact assignee stack; used in the workflow header and collaborator pickers. */
export const WorkflowAssigneesButton = forwardRef(function WorkflowAssigneesButton(
  { assignees, className, dataCy, max, onClick, open }: WorkflowAssigneesButtonProps,
  ref: React.Ref<HTMLDivElement>
): JSX.Element {
  const { classes } = useButtonStyles({
    numAssignees: assignees.length,
    selected: open,
  });

  return (
    <div ref={ref} className={clsx(classes.button, className)} data-cy={dataCy} onClick={onClick}>
      <WorkflowAssigneesAvatarGroup assignees={assignees} max={max} />
    </div>
  );
});

interface WorkflowAssigneesAvatarGroupProps {
  assignees: WorkflowPermissionSubset[];
  max?: number;
}

export function WorkflowAssigneesAvatarGroup({
  assignees,
  max = 3,
}: WorkflowAssigneesAvatarGroupProps): JSX.Element {
  const sortedAssignees = [...assignees].sort((a, b) =>
    getAssigneeTitle(a).localeCompare(getAssigneeTitle(b))
  );

  if (!sortedAssignees.length)
    return (
      <AlineAvatarGroup>
        <AlineTooltip arrow placement="top" title="Unassigned">
          <AlineAvatar />
        </AlineTooltip>
      </AlineAvatarGroup>
    );

  return (
    <AlineAvatarGroup max={max}>
      {sortedAssignees.map(assignee => (
        <AlineTooltip key={assignee.id} arrow placement="top" title={getAssigneeTitle(assignee)}>
          <AlineAvatar avatarInfo={getPermissionAvatarInfo(assignee)} />
        </AlineTooltip>
      ))}
    </AlineAvatarGroup>
  );
}

void gql`
  mutation UpdateWorkflowAssignee($input: UpdateWorkflowPermissionInput!) {
    updateWorkflowPermission(input: $input) {
      __typename
      id
      isAssignee
    }
  }
`;

export function useUpdateWorkflowAssignee() {
  const [update, { loading }] = Apollo.useUpdateWorkflowAssigneeMutation();
  const updateWorkflowAssignee = useCallback(
    async (args: { isAssignee: boolean; workflowPermissionId: UUID }) => {
      const { isAssignee, workflowPermissionId } = args;
      return update({
        optimisticResponse: {
          __typename: 'Mutation',
          updateWorkflowPermission: {
            __typename: 'WorkflowPermission',
            id: workflowPermissionId,
            isAssignee,
          },
        },
        variables: { input: { props: { isAssignee }, workflowPermissionId } },
      });
    },
    [update]
  );
  return { loading, updateWorkflowAssignee };
}

interface WorkflowAssigneesChooserWorkflow extends Pick<
  Apollo.Workflow,
  'id' | 'status' | 'variant' | '__typename'
> {
  owner: UserWithOrg;
  permissions: WorkflowPermissionSubset[];
}

interface WorkflowAssigneesChooserProps {
  workflow: WorkflowAssigneesChooserWorkflow;
  anchorOrigin?: PopoverProps['anchorOrigin'];
  apiRef?: React.RefObject<GridApi | null>;
  dataCy?: string;
  max?: number;
  transformOrigin?: PopoverProps['transformOrigin'];
}

export function WorkflowAssigneesChooser({
  anchorOrigin,
  apiRef: _apiRef,
  dataCy,
  max,
  transformOrigin,
  workflow,
}: WorkflowAssigneesChooserProps): JSX.Element {
  const dispatchNotAuthorized = useDispatchNotification({
    message: 'Only workflow editors can edit assignees.',
    variant: 'error',
  });
  const currentUser = useSelectCurrentUser();
  const { updateWorkflowAssignee } = useUpdateWorkflowAssignee();
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [open, setOpen] = useState<'add-collab' | 'popper' | null>(null);
  const { checkWorkflowAuthorization } = useGetCheckWorkflowAuthorization();
  const { canEditWorkflow } = checkWorkflowAuthorization(workflow, currentUser);
  const isContractWorkflow = workflow.variant === Apollo.WorkflowVariant.Contract;
  const safeAssignees = workflow.permissions.filter(getIsVisibleWorkflowAssignee);
  const isPopperOpen = open === 'popper';

  return (
    <div
      onClick={e => {
        // Prevent clicks in the Popper or modal from propagating to prevent weird UX.
        e.stopPropagation();
      }}
    >
      <WorkflowAssigneesButton
        ref={setAnchorEl}
        assignees={safeAssignees}
        dataCy={dataCy}
        max={max}
        open={isPopperOpen}
        onClick={() => setOpen(!isPopperOpen ? 'popper' : null)}
      />
      <WorkflowCollaboratorsPopper
        anchorEl={anchorEl}
        anchorOrigin={anchorOrigin}
        open={isPopperOpen}
        renderOption={option => <WorkflowAssigneeOption option={option} />}
        transformOrigin={transformOrigin}
        workflow={workflow}
        onChange={async (_, option) => {
          if (!option) return;
          if (!canEditWorkflow) return dispatchNotAuthorized();
          if (option.__typename === 'NewCollaborator') return setOpen('add-collab');

          const isAlreadyAssignee = safeAssignees.some(a => a.id === option.id);
          await updateWorkflowAssignee({
            isAssignee: !isAlreadyAssignee,
            workflowPermissionId: option.id,
          });
        }}
        onClose={() => setOpen(null)}
      />
      <AddWorkflowCollaboratorDialog
        afterSubmit={async permissions => {
          await Promise.all(
            permissions.map(permission => {
              if (permission.account?.__typename === 'Org') return;

              return updateWorkflowAssignee({
                isAssignee: true,
                workflowPermissionId: permission.id,
              });
            })
          );
        }}
        filterAccountsPred={account => account.__typename !== 'Org'}
        open={open === 'add-collab'}
        permissionProps={{
          timeliness: isContractWorkflow
            ? Apollo.WorkflowPermissionTimeliness.Active
            : Apollo.WorkflowPermissionTimeliness.Queued,
        }}
        templateTimelinessCheck={Apollo.WorkflowPermissionTimeliness.Queued}
        variant={
          isContractWorkflow
            ? AddCollaboratorDialogVariant.NoTabs
            : AddCollaboratorDialogVariant.Tabs
        }
        workflow={workflow}
        onClose={() => setOpen(null)}
      />
    </div>
  );
}

export function getIsVisibleWorkflowAssignee(permission: WorkflowPermissionSubset): boolean {
  return (
    permission.isAssignee &&
    Boolean(permission.account || permission.roleName || permission.isLauncher)
  );
}

interface WorkflowAssigneeOptionProps {
  option: CollaboratorOption;
}

export function WorkflowAssigneeOption({ option }: WorkflowAssigneeOptionProps): JSX.Element {
  const { classes } = useCollabOptionStyles();
  const isNewCollaboratorOption = option.__typename === 'NewCollaborator';

  if (isNewCollaboratorOption) return <NewCollaboratorOption newCollaboratorText="Add Assignee" />;

  const { avatarColor, primaryText, secondaryText } = getCollaboratorDisplayDetails(option);

  return (
    <div className={classes.optionContainer}>
      <AlineCheckbox checked={option.isAssignee} />
      <AlineAvatar
        avatarInfo={getPermissionAvatarInfo(option)}
        styles={{ backgroundColor: avatarColor, size: 'md' }}
      />
      <ListItemText
        classes={{ secondary: classes.secondaryText }}
        primary={primaryText}
        secondary={secondaryText}
        slotProps={{
          primary: { align: 'left', noWrap: true, variant: 'body1' },
          secondary: { align: 'left', noWrap: true },
        }}
      />
    </div>
  );
}

// "Name (Role)" for a filled seat; the launcher placeholder has neither, so it goes by its label.
function getAssigneeTitle(permission: WorkflowPermissionSubset): string {
  if (permission.isLauncher) return WORKFLOW_LAUNCHER_LABEL;
  return getAssignmentString({
    assignee: permission.account?.__typename === 'User' ? permission.account : null,
    assigneeRoleName: permission.roleName,
  });
}
